import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useBookingSettings } from "@/hooks/use-booking-settings";
import { useLocalizedContent } from "@/hooks/use-localized-content";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { format, addDays, Locale } from "date-fns";
import { pt, enUS, fr, es, it } from "date-fns/locale";

const dateLocaleMap: Record<string, Locale> = {
  "pt-PT": pt, "pt-BR": pt, en: enUS, fr: fr, es: es, it: it,
};
import { hasRGPDConsent } from "@/components/RGPDConsent";
import {
  CalendarDays, Clock, User, Mail, Phone, MapPin, Locate,
  Home, Building2, CheckCircle, ArrowRight, ArrowLeft, Euro,
  Heart, Shield, ShieldAlert, Users, Layers,
  Scissors, Sparkles, Hand, Eye, Palette, Footprints,
  Flower2, Droplets, Flame, Baby, Leaf,
} from "lucide-react";
import MiniMap, { geocodeAddress } from "@/components/booking/MiniMap";

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
  available_at_home: boolean;
  available_at_studio: boolean;
  image_url: string | null;
  category?: string | null;
}

interface Zone {
  id: string;
  name: string;
  base_fee: number;
}

const timeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00",
];

const BookingSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { c, content: cmsContent } = useLocalizedContent();
  const { settings: bookingSettings } = useBookingSettings();
  const { t, i18n } = useTranslation();
  const dateLocale = dateLocaleMap[i18n.language] || pt;
  const bookingTitle = c("booking_title", "booking.title");
  const bookingSubtitle = c("booking_subtitle", "booking.subtitle");
  const [consentGiven, setConsentGiven] = useState(hasRGPDConsent());

  // Listen for consent changes (localStorage updated by RGPDConsent component)
  useEffect(() => {
    const checkConsent = () => setConsentGiven(hasRGPDConsent());
    window.addEventListener("storage", checkConsent);
    // Also poll briefly since storage event doesn't fire in same tab
    const interval = setInterval(checkConsent, 1000);
    return () => { window.removeEventListener("storage", checkConsent); clearInterval(interval); };
  }, []);

  // New order: 0=Date/Time, 1=Service, 2=Gender, 3=ClientInfo, 4=Location, 5=Confirm
  const [step, setStep] = useState(0);
  const [services, setServices] = useState<Service[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [loadingGps, setLoadingGps] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [gender, setGender] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [locationType, setLocationType] = useState<"home" | "studio">("studio");
  const [address, setAddress] = useState("");
  const [selectedZone, setSelectedZone] = useState("");
  const [notes, setNotes] = useState("");
  const [homeCoords, setHomeCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [homeMapLoading, setHomeMapLoading] = useState(false);
  const [studioCoords, setStudioCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [staffList, setStaffList] = useState<{ id: string; name: string; specialties: string | null }[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedStaffName, setSelectedStaffName] = useState("");
  const [staffServiceIds, setStaffServiceIds] = useState<string[] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [genderOptions, setGenderOptions] = useState<Array<{id: string; label: string; sublabel?: string; icon: string; image_url?: string; action: string}>>([
    { id: "female", label: "Mulher", icon: "Heart", action: "select" },
    { id: "child", label: "Criança", sublabel: "(menina)", icon: "Flower2", action: "select" },
    { id: "male", label: "Homem", icon: "User", action: "select" },
    { id: "other", label: "Outro", icon: "Shield", action: "block" },
  ]);
  const [genderStepEnabled, setGenderStepEnabled] = useState(true);
  const [genderStepTitle, setGenderStepTitle] = useState("Informação do paciente");
  const [genderStepDescription, setGenderStepDescription] = useState("A Mente Sã é um espaço dedicado ao bem-estar feminino e infantil. Por favor, indique o perfil do paciente para que possamos oferecer o melhor atendimento.");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const genderIconMap: Record<string, any> = {
    Heart, Flower2, Shield, User, Baby, Sparkles, Eye, Hand,
  };

  useEffect(() => {
    supabase.from("services").select("*").eq("is_active", true).then(({ data }) => {
      setServices((data as Service[]) || []);
    });
    supabase.from("delivery_zones").select("id, name, base_fee").eq("is_active", true).then(({ data }) => {
      setZones((data as Zone[]) || []);
    });
    supabase.from("staff_public").select("id, name, specialties").eq("is_active", true).order("name").then(({ data }) => {
      setStaffList((data as { id: string; name: string; specialties: string | null }[]) || []);
    });
    // Load gender options from settings
    supabase.from("app_settings").select("key, value").in("key", ["gender_options", "gender_step_title", "gender_step_description", "gender_step_enabled"]).then(({ data }) => {
      (data || []).forEach((row: any) => {
        if (row.key === "gender_options" && Array.isArray(row.value)) {
          setGenderOptions(row.value);
        }
        if (row.key === "gender_step_title" && typeof row.value === "string") {
          setGenderStepTitle(row.value);
        }
        if (row.key === "gender_step_description" && typeof row.value === "string") {
          setGenderStepDescription(row.value);
        }
        if (row.key === "gender_step_enabled") {
          setGenderStepEnabled(row.value !== false && row.value !== "false");
        }
      });
    });
  }, []);

  useEffect(() => {
    if (user) {
      setClientName(user.user_metadata?.full_name || "");
      setClientEmail(user.email || "");
    }
  }, [user]);

  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    supabase
      .rpc("get_booked_slots", {
        p_date: dateStr,
        p_staff_id: selectedStaffId || null,
      })
      .then(({ data }) => {
        setBookedSlots((data as string[]) || []);
      });
  }, [selectedDate, selectedStaffId]);

  // Fetch staff-specific services when staff changes
  useEffect(() => {
    if (!selectedStaffId) {
      setStaffServiceIds(null);
      return;
    }
    supabase
      .from("staff_services")
      .select("service_id")
      .eq("staff_id", selectedStaffId)
      .then(({ data }) => {
        if (data && data.length > 0) {
          setStaffServiceIds(data.map((r: any) => r.service_id));
        } else {
          setStaffServiceIds(null); // no assignments = all services
        }
      });
  }, [selectedStaffId]);

  const zone = zones.find((z) => z.id === selectedZone);
  const deliveryFee = locationType === "home" ? (zone?.base_fee || 0) : 0;
  const servicesSubtotal = selectedServices.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration_minutes, 0);
  const total = servicesSubtotal + deliveryFee;
  const primaryService = selectedServices.length > 0 ? selectedServices[0] : null;
  // Calculate minimum booking date based on settings.
  // If same-day booking is allowed OR min_advance_hours is less than 24h,
  // today is a valid selectable date (time-slot filter enforces the hourly limit).
  // Otherwise, the earliest valid day is tomorrow (at start of day, not "24h from now").
  const minBookingDate = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (bookingSettings.allow_same_day || bookingSettings.min_advance_hours < 24) {
      return startOfToday;
    }
    return new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);
  }, [bookingSettings.allow_same_day, bookingSettings.min_advance_hours]);

  // Filter time slots based on buffer and same-day advance rules
  const getFilteredTimeSlots = (slots: string[], date: Date | undefined): string[] => {
    if (!date) return slots;
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    return slots.filter((slot) => {
      // For same-day bookings, enforce minimum advance hours regardless of the
      // allow_same_day flag (if today is disabled entirely, this branch is unreachable).
      if (isToday) {
        const [h, m] = slot.split(":").map(Number);
        const slotTime = new Date(date);
        slotTime.setHours(h, m, 0, 0);
        const minTime = new Date(now.getTime() + bookingSettings.min_advance_hours * 60 * 60 * 1000);
        if (slotTime < minTime) return false;
      }
      
      // Check buffer conflicts with booked slots
      if (bookedSlots.length > 0 && (bookingSettings.buffer_before > 0 || bookingSettings.buffer_after > 0)) {
        const [slotH, slotM] = slot.split(":").map(Number);
        const slotMinutes = slotH * 60 + slotM;
        
        for (const booked of bookedSlots) {
          const [bH, bM] = booked.split(":").map(Number);
          const bookedMinutes = bH * 60 + bM;
          
          // Block slots within buffer_before minutes before a booked slot
          if (bookingSettings.buffer_before > 0) {
            if (slotMinutes >= bookedMinutes - bookingSettings.buffer_before && slotMinutes < bookedMinutes) {
              return false;
            }
          }
          // Block slots within buffer_after minutes after a booked slot
          if (bookingSettings.buffer_after > 0) {
            if (slotMinutes > bookedMinutes && slotMinutes <= bookedMinutes + bookingSettings.buffer_after) {
              return false;
            }
          }
        }
      }
      
      return true;
    });
  };

  const filteredTimeSlots = getFilteredTimeSlots(timeSlots, selectedDate);

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: t("booking.gpsUnavailable"), variant: "destructive" });
      return;
    }
    setLoadingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}&addressdetails=1`
          );
          const data = await res.json();
          setAddress(data.display_name || `${position.coords.latitude}, ${position.coords.longitude}`);
          setHomeCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
          toast({ title: t("booking.locationObtained") });
        } catch {
          setAddress(`${position.coords.latitude}, ${position.coords.longitude}`);
          setHomeCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        }
        setLoadingGps(false);
      },
      () => {
        toast({ title: t("booking.gpsError"), description: t("booking.checkPermissions"), variant: "destructive" });
        setLoadingGps(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Debounced geocode when the user types the home address
  useEffect(() => {
    if (locationType !== "home") return;
    if (!address || address.trim().length < 6) { setHomeCoords(null); return; }
    setHomeMapLoading(true);
    const timer = setTimeout(async () => {
      const coords = await geocodeAddress(address);
      setHomeCoords(coords);
      setHomeMapLoading(false);
    }, 700);
    return () => clearTimeout(timer);
  }, [address, locationType]);

  // Studio address + coords (from CMS). Prefer stored lat/lng, fall back to geocoding.
  const studioAddress = c("studio_address", "", "Rua Indiveri Colucci 14, Paço de Arcos, Oeiras, Portugal");
  const studioImage = cmsContent["studio_image"] || "";
  const storedLat = parseFloat(cmsContent["studio_address_lat"] || "");
  const storedLng = parseFloat(cmsContent["studio_address_lng"] || "");
  useEffect(() => {
    if (isFinite(storedLat) && isFinite(storedLng)) {
      setStudioCoords({ lat: storedLat, lng: storedLng });
      return;
    }
    if (!studioAddress) return;
    let cancelled = false;
    geocodeAddress(studioAddress).then((coords) => { if (!cancelled) setStudioCoords(coords); });
    return () => { cancelled = true; };
  }, [studioAddress, storedLat, storedLng]);

  const handleSubmit = async () => {
    if (selectedServices.length === 0 || !selectedDate || !selectedTime) return;
    if (!clientName.trim() || !clientEmail.trim() || !clientPhone.trim()) return;
    if (locationType === "home" && !address.trim()) return;
    setSubmitting(true);
    const { data: booking, error } = await supabase.from("bookings").insert({
      client_id: user?.id ?? null,
      service_id: selectedServices[0].id,
      client_name: clientName,
      client_email: clientEmail,
      client_phone: clientPhone,
      booking_date: format(selectedDate, "yyyy-MM-dd"),
      booking_time: selectedTime,
      location_type: locationType,
      address: locationType === "home" ? address : t("booking.studioName"),
      zone_id: locationType === "home" && selectedZone ? selectedZone : null,
      delivery_fee: deliveryFee,
      total_price: total,
      notes,
      status: "pending",
      staff_id: selectedStaffId || null,
      locale: i18n.language || "pt-PT",
    }).select("id").single();
    if (error) {
      toast({ title: t("booking.error"), description: error.message, variant: "destructive" });
    } else {
      // Insert booking_services junction for multi-service
      if (booking && selectedServices.length > 1) {
        await supabase.from("booking_services").insert(
          selectedServices.map((s) => ({
            booking_id: booking.id,
            service_id: s.id,
            service_name: s.name,
            service_price: s.price,
          }))
        );
      }
      setDone(true);
      toast({ title: t("booking.reserveCreated"), description: t("booking.awaitConfirmation") });
      try {
        await supabase.functions.invoke("notify-booking-status", {
          body: {
            booking_id: "new",
            status: "pending",
            client_email: clientEmail,
            client_name: clientName,
            client_phone: clientPhone,
            service_name: selectedServices.map(s => s.name).join(" + "),
            booking_date: format(selectedDate!, "yyyy-MM-dd"),
            booking_time: selectedTime,
            staff_name: selectedStaffName || "",
            staff_id: selectedStaffId || null,
            notify_staff: !!selectedStaffId,
            booking_address: locationType === "home" ? address : "Espaço Mente Sã",
            total_price: total,
            location_type: locationType,
            locale: i18n.language || "pt-PT",
          },
        });
      } catch (e) {
        console.error("Notification error:", e);
      }
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setDone(false); setBlocked(false); setStep(0); setGender("");
    setSelectedServices([]); setSelectedDate(undefined); setSelectedTime("");
    setNotes(""); setAddress(""); setSelectedZone(""); setLocationType("studio");
    setSelectedStaffId(""); setSelectedStaffName(""); setSelectedCategory("");
  };

  // Extract unique categories from staff that have specialties (support comma-separated)
  const categories = Array.from(
    new Set(
      staffList.flatMap((s) =>
        s.specialties ? s.specialties.split(",").map(c => c.trim()).filter(Boolean) : []
      )
    )
  ).sort();
  const showCategoryStep = categories.length > 0;

  // Filter staff by selected category (staff can have multiple categories)
  const filteredStaff = selectedCategory
    ? staffList.filter((s) => {
        if (!s.specialties) return false;
        const cats = s.specialties.split(",").map(c => c.trim());
        return cats.includes(selectedCategory);
      })
    : staffList;
  const showStaffStep = false; // merged into unified step

  // Auto-select if only 1 staff in category
  useEffect(() => {
    if (selectedCategory && filteredStaff.length === 1 && selectedStaffId !== filteredStaff[0].id) {
      setSelectedStaffId(filteredStaff[0].id);
      setSelectedStaffName(filteredStaff[0].name);
    }
  }, [selectedCategory, filteredStaff]);

  // Dynamic step mapping - skip gender step if disabled
  const unifiedStepIndex = 0;
  const serviceStepIndex = 1;
  const genderStepIndex = genderStepEnabled ? 2 : -1; // -1 means skipped
  const clientStepIndex = genderStepEnabled ? 3 : 2;
  const locationStepIndex = genderStepEnabled ? 4 : 3;
  const confirmStep = genderStepEnabled ? 5 : 4;

  const stepTitles = [
    { icon: CalendarDays, label: t("booking.dateLabel") },
    { icon: Heart, label: t("booking.service") },
    ...(genderStepEnabled ? [{ icon: Shield, label: t("booking.selectCategory", { defaultValue: "Profile" }) }] : []),
    { icon: User, label: t("booking.name") },
    { icon: MapPin, label: t("booking.location") },
    { icon: CheckCircle, label: t("booking.confirm") },
  ];

  // If a staff has explicit service assignments, filter to those; but if the
  // intersection is empty (e.g. all assigned services are inactive) fall back
  // to showing all active services so the client is never blocked.
  const staffFiltered = staffServiceIds
    ? services.filter((s) => staffServiceIds.includes(s.id))
    : services;
  const categoryFiltered = selectedCategory
    ? staffFiltered.filter((s) => (s.category || "").toLowerCase() === selectedCategory.toLowerCase())
    : staffFiltered;
  const filteredServices = categoryFiltered.length > 0 ? categoryFiltered : (staffFiltered.length > 0 ? staffFiltered : services);

  const canAdvanceMap: Record<number, boolean> = {
    [unifiedStepIndex]: !!selectedDate && selectedTime !== "" && (!showCategoryStep || selectedCategory !== ""),
    [serviceStepIndex]: selectedServices.length > 0,
    ...(genderStepEnabled ? { [genderStepIndex]: gender !== "" } : {}),
    [clientStepIndex]: clientName.trim() !== "" && clientEmail.trim() !== "" && clientPhone.trim() !== "",
    [locationStepIndex]: locationType === "studio" || (address.trim() !== "" && selectedZone !== ""),
    [confirmStep]: true,
  };

  const canAdvance = Array.from({ length: confirmStep + 1 }, (_, i) => canAdvanceMap[i] ?? true);

  // Scroll to booking section when step changes (skip initial mount)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const el = document.getElementById("agendar");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  if (done) {
    return (
      <section id="agendar" className="py-24 min-h-[70vh] flex items-center">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            onAnimationComplete={() => {
              document.getElementById("agendar")?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="max-w-lg mx-auto text-center rounded-3xl bg-card border border-border p-12">
            <CheckCircle size={56} className="text-primary mx-auto mb-6" />
            <h2 className="font-display text-3xl font-bold mb-3">{t("booking.reserveSent")}</h2>
            <p className="text-muted-foreground mb-8">{t("booking.reservePending")}</p>
            <Button className="rounded-full" onClick={resetForm}>{t("booking.newBooking")}</Button>
          </motion.div>
        </div>
      </section>
    );
  }

  if (blocked) {
    return (
      <section id="agendar" className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-lg mx-auto text-center rounded-3xl bg-card border border-border p-12">
            <Shield size={48} className="text-primary mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold mb-3">{t("booking.importantInfo")}</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              {t("booking.blockedMessage")}
            </p>
            <Button variant="outline" className="rounded-full" onClick={resetForm}>{t("booking.goBack")}</Button>
          </div>
        </div>
      </section>
    );
  }

  if (!consentGiven) {
    return (
      <section id="agendar" className="py-24 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-lg mx-auto text-center rounded-3xl bg-card border border-border p-12">
            <ShieldAlert size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold mb-3">{t("booking.consentRequired")}</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              {t("booking.consentRequired")}
            </p>
            <Button className="rounded-full" onClick={() => { localStorage.removeItem("rgpd_consent"); window.location.reload(); }}>
              {t("booking.acceptPrivacy")}
            </Button>
          </div>
        </div>
      </section>
    );
  }


  return (
    <section id="agendar" className="py-12 md:py-16 bg-secondary/30">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-6">
          <span className="inline-block rounded-full bg-mint px-4 py-1.5 text-xs font-semibold text-accent-foreground mb-3">{t("booking.onlineBooking")}</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-2">{bookingTitle}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">{bookingSubtitle}</p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          {/* Progress */}
          <div className="flex items-center justify-between mb-4 px-2">
            {stepTitles.map((s, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  i < step ? "bg-primary text-primary-foreground" :
                  i === step ? "bg-primary text-primary-foreground ring-4 ring-primary/20" :
                  "bg-muted text-muted-foreground"
                }`}>
                  {i < step ? <CheckCircle size={16} /> : <s.icon size={16} />}
                </div>
                <span className={`text-[10px] font-medium hidden sm:block ${i <= step ? "text-primary" : "text-muted-foreground"}`}>{s.label}</span>
              </div>
            ))}
          </div>

          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="rounded-3xl bg-card border border-border p-5 md:p-6 shadow-sm">

            <div>
            {/* Unified Step: Category + Date + Staff + Time */}
            {step === unifiedStepIndex && (
              <div className="space-y-5">
                <h3 className="font-display text-xl font-semibold">{t("booking.chooseDateStaffTime")}</h3>

                {/* Category chips */}
                {showCategoryStep && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">{t("booking.whatServiceType")}</p>
                    <div className="flex flex-wrap justify-center gap-3">
                      {categories.map((cat) => {
                        const iconMap: Record<string, React.ReactNode> = {
                          "Massagista": <Hand size={22} />,
                          "Cabeleireira": <Scissors size={22} />,
                          "Manicure": <Sparkles size={22} />,
                          "Sobrancelha": <Eye size={22} />,
                          "Maquiagem": <Palette size={22} />,
                          "Estética": <Heart size={22} />,
                          "Podologia": <Footprints size={22} />,
                        };
                        const icon = iconMap[cat] || <Layers size={22} />;
                        const isActive = selectedCategory === cat;

                        // After selection: show only the active category as text
                        if (selectedCategory && !isActive) {
                          return null;
                        }
                        if (selectedCategory && isActive) {
                          return (
                            <button
                              key={cat}
                              onClick={() => {
                                setSelectedCategory("");
                                setSelectedStaffId("");
                                setSelectedStaffName("");
                                setSelectedTime("");
                                setSelectedServices([]);
                              }}
                              className="rounded-full px-4 py-2 text-sm font-semibold border-2 border-primary bg-primary text-primary-foreground transition-all"
                            >
                              {cat} 
                            </button>
                          );
                        }

                        // Before selection: full cards with icons
                        return (
                          <button
                            key={cat}
                            onClick={() => {
                              setSelectedCategory(cat);
                              setSelectedStaffId("");
                              setSelectedStaffName("");
                              setSelectedTime("");
                              setSelectedServices([]);
                            }}
                            className={`group flex flex-col items-center gap-2 rounded-2xl border-2 p-4 transition-all duration-200 w-[calc(50%-0.375rem)] sm:w-[calc(25%-0.5625rem)] max-w-[150px] ${
                              isActive
                                ? "border-primary bg-primary/10 shadow-md shadow-primary/10"
                                : "border-border bg-card hover:border-primary/40 hover:shadow-sm"
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                              isActive
                                ? "bg-primary text-primary-foreground"
                                : "bg-accent text-muted-foreground group-hover:text-primary group-hover:bg-primary/10"
                            }`}>
                              {icon}
                            </div>
                            <span className={`text-sm font-semibold transition-colors ${
                              isActive ? "text-primary" : "text-foreground"
                            }`}>{cat}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Calendar + Staff + Time grid */}
                {(!showCategoryStep || selectedCategory) && (
                  <div className="grid md:grid-cols-[1fr_auto] gap-6">
                    {/* Left: Calendar */}
                    <div>
                      <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2"><CalendarDays size={14} /> {t("booking.dateLabel")}</p>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(d) => { setSelectedDate(d); setSelectedTime(""); }}
                        disabled={(date) => date < minBookingDate || date.getDay() === 0}
                        locale={dateLocale}
                        className="rounded-xl border border-border pointer-events-auto"
                      />
                    </div>

                    {/* Right: Staff + Time */}
                    <div className="space-y-4 min-w-0 md:min-w-[240px]">
                      {/* Staff selection */}
                      {selectedDate && filteredStaff.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2"><Users size={14} /> {t("booking.professionalLabel")}</p>
                          {filteredStaff.length > 3 ? (
                            /* Tab style for 4+ staff */
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                onClick={() => { setSelectedStaffId(""); setSelectedStaffName(""); setSelectedTime(""); }}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                  selectedStaffId === "" ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground hover:border-primary/40"
                                }`}
                              >
                                {t("booking.anyAvailable")}
                              </button>
                              {filteredStaff.map((s) => (
                                <button
                                  key={s.id}
                                  onClick={() => { setSelectedStaffId(s.id); setSelectedStaffName(s.name); setSelectedTime(""); }}
                                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                    selectedStaffId === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground hover:border-primary/40"
                                  }`}
                                >
                                  {s.name}
                                </button>
                              ))}
                            </div>
                          ) : (
                            /* Card style for ≤3 staff */
                            <div className="space-y-1.5">
                              <button
                                onClick={() => { setSelectedStaffId(""); setSelectedStaffName(""); setSelectedTime(""); }}
                                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-all ${
                                  selectedStaffId === "" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                                }`}
                              >
                                <span className="text-foreground font-medium">{t("booking.noPreference")}</span>
                              </button>
                              {filteredStaff.map((s) => (
                                <button
                                  key={s.id}
                                  onClick={() => { setSelectedStaffId(s.id); setSelectedStaffName(s.name); setSelectedTime(""); }}
                                  className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-all ${
                                    selectedStaffId === s.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                      <span className="text-xs font-semibold text-primary">{s.name.charAt(0).toUpperCase()}</span>
                                    </div>
                                    <span className="text-foreground font-medium">{s.name}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Time slots */}
                      {selectedDate && (
                        <div>
                          <p className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                            <Clock size={14} /> {t("booking.timeLabel")}, {format(selectedDate, "d MMM", { locale: dateLocale })}
                          </p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {filteredTimeSlots.length === 0 ? (
                              <p className="col-span-3 text-sm text-muted-foreground py-4 text-center">{t("booking.noSlots")}</p>
                            ) : (
                              filteredTimeSlots.map((slot) => {
                                const isBooked = bookedSlots.includes(slot);
                                return (
                                  <button key={slot} disabled={isBooked} onClick={() => setSelectedTime(slot)}
                                    className={`rounded-lg py-2 text-xs font-medium transition-all ${
                                      isBooked ? "bg-muted text-muted-foreground/40 cursor-not-allowed line-through"
                                      : selectedTime === slot ? "bg-primary text-primary-foreground"
                                      : "border border-border hover:border-primary/50"
                                    }`}>{slot}</button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}

                      {!selectedDate && (
                        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                          <p> {t("booking.selectADate")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}




            {/* Service, Multi-select */}
            {step === serviceStepIndex && (
              <div className="space-y-5">
                <div className="text-center mb-2">
                   <h3 className="font-display text-xl font-semibold">{t("booking.whatTreatment")}</h3>
                   <p className="text-sm text-muted-foreground mt-1">{t("booking.selectOneOrMore")}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredServices.map((s) => {
                    const isSelected = selectedServices.some((x) => x.id === s.id);
                    const toggleSvc = () => {
                      setSelectedServices((prev) =>
                        isSelected ? prev.filter((x) => x.id !== s.id) : [...prev, s]
                      );
                    };
                    return (
                      <button key={s.id} onClick={toggleSvc}
                        className={`group relative text-left rounded-2xl border overflow-hidden transition-all ${
                          isSelected
                            ? "border-primary ring-2 ring-primary/20 shadow-md" 
                            : "border-border hover:border-primary/40 hover:shadow-sm"
                        }`}>
                        {/* Image area */}
                        <div className="relative h-16 bg-gradient-to-br from-primary/10 to-accent/30 overflow-hidden">
                          {s.image_url ? (
                            <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center relative">
                              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/10 to-primary/5" />
                              <div className="relative w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                {(() => {
                                  const name = s.name.toLowerCase();
                                  const iconClass = "text-primary/60";
                                  if (name.includes("relaxa")) return <Flower2 size={20} className={iconClass} />;
                                  if (name.includes("desport")) return <Hand size={20} className={iconClass} />;
                                  if (name.includes("pedras") || name.includes("quentes")) return <Flame size={20} className={iconClass} />;
                                  if (name.includes("aroma")) return <Droplets size={20} className={iconClass} />;
                                  if (name.includes("reflexo") || name.includes("podal")) return <Footprints size={20} className={iconClass} />;
                                  if (name.includes("infanti") || name.includes("criança") || name.includes("bebé")) return <Baby size={20} className={iconClass} />;
                                  if (name.includes("facial") || name.includes("rosto")) return <Eye size={20} className={iconClass} />;
                                  return <Leaf size={20} className={iconClass} />;
                                })()}
                              </div>
                            </div>
                          )}
                          {/* Price badge */}
                          <div className="absolute top-2 right-2 bg-background/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm">
                            <span className="flex items-center gap-0.5 text-primary font-bold text-sm"><Euro size={12} />{s.price.toFixed(2)}</span>
                          </div>
                          {isSelected && (
                            <div className="absolute top-2 left-2 bg-primary text-primary-foreground rounded-full p-1">
                              <CheckCircle size={14} />
                            </div>
                          )}
                        </div>
                        {/* Info */}
                        <div className="p-3.5">
                          <h4 className="font-display font-semibold text-foreground text-sm leading-tight mb-1">{s.name}</h4>
                          {s.description && (
                            <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2 mb-2">{s.description}</p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1 bg-accent/50 px-2 py-0.5 rounded-full"><Clock size={10} /> {s.duration_minutes}min</span>
                             {s.available_at_home && <span className="flex items-center gap-1 bg-accent/50 px-2 py-0.5 rounded-full"><Home size={10} /> {t("booking.home")}</span>}
                             {s.available_at_studio && <span className="flex items-center gap-1 bg-accent/50 px-2 py-0.5 rounded-full"><Building2 size={10} /> {t("booking.studio")}</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {filteredServices.length === 0 && <p className="text-center text-muted-foreground py-8">{t("booking.noServicesAvailable")}</p>}
                
                {/* Selection summary */}
                {selectedServices.length > 0 && (
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-1">
                    <div className="flex justify-between text-sm">
                       <span className="text-muted-foreground">{selectedServices.length} {t("booking.services")} · {totalDuration} min</span>
                      <span className="text-foreground font-bold">€{servicesSubtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedServices.map(s => (
                        <span key={s.id} className="bg-accent/50 text-xs px-2 py-0.5 rounded-full">{s.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Gender */}
            {step === genderStepIndex && (
              <div className="space-y-5">
                <h3 className="font-display text-xl font-semibold">{genderStepTitle}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{genderStepDescription}</p>
                <div className={`grid gap-4 ${genderOptions.length === 2 ? 'grid-cols-2' : genderOptions.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                  {genderOptions.map((opt) => {
                    const IconComp = genderIconMap[opt.icon] || Shield;
                    const isBlock = opt.action === "block";
                    const isActive = gender === opt.id;

                    return (
                      <button
                        key={opt.id}
                        onClick={() => isBlock ? setBlocked(true) : setGender(opt.id)}
                        className={`rounded-2xl border-2 p-6 text-center transition-all duration-300 ${
                          isBlock
                            ? "border-border/60 opacity-50 hover:opacity-70 hover:border-muted-foreground/30"
                            : isActive
                              ? "border-primary bg-gradient-to-b from-primary/10 to-primary/5 shadow-lg shadow-primary/10"
                              : "border-border hover:border-primary/40 hover:shadow-sm"
                        }`}
                      >
                        {opt.image_url ? (
                          <div className="w-14 h-14 rounded-2xl mx-auto mb-3 overflow-hidden">
                            <img src={opt.image_url} alt={opt.label} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className={`w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center transition-colors ${
                            isBlock
                              ? "bg-muted text-muted-foreground"
                              : isActive
                                ? "bg-primary text-primary-foreground"
                                : "bg-accent text-primary/70"
                          }`}>
                            <IconComp size={24} />
                          </div>
                        )}
                        <span className={`font-display font-semibold text-sm ${isBlock ? "text-muted-foreground" : "text-foreground"}`}>
                          {opt.label}
                        </span>
                        {opt.sublabel && (
                          <span className="block text-[10px] text-muted-foreground mt-0.5">{opt.sublabel}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Client Info */}
            {step === clientStepIndex && (
              <div className="space-y-5">
                <h3 className="font-display text-xl font-semibold">{t("booking.name")}</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bk-name" className="flex items-center gap-2 text-sm"><User size={14} /> {t("booking.name")}</Label>
                    <Input id="bk-name" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder={t("booking.namePlaceholder")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bk-email" className="flex items-center gap-2 text-sm"><Mail size={14} /> {t("booking.email")}</Label>
                    <Input id="bk-email" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="email@exemplo.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bk-phone" className="flex items-center gap-2 text-sm"><Phone size={14} /> {t("booking.phone")}</Label>
                    <PhoneInput id="bk-phone" value={clientPhone} onChange={setClientPhone} placeholder="9XX XXX XXX" />
                  </div>
                </div>
              </div>
            )}

            {/* Location */}
            {step === locationStepIndex && selectedServices.length > 0 && (
              <div className="space-y-5">
                <h3 className="font-display text-xl font-semibold">{t("booking.location")}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {selectedServices.some(s => s.available_at_studio) && (
                    <Button variant={locationType === "studio" ? "default" : "outline"} className="h-20 rounded-xl flex flex-col gap-1" onClick={() => setLocationType("studio")}>
                      <Building2 size={22} /><span className="text-xs">{t("booking.studioName")}</span>
                    </Button>
                  )}
                  {selectedServices.some(s => s.available_at_home) && (
                    <Button variant={locationType === "home" ? "default" : "outline"} className="h-20 rounded-xl flex flex-col gap-1" onClick={() => setLocationType("home")}>
                      <Home size={22} /><span className="text-xs">{t("booking.home")}</span>
                    </Button>
                  )}
                </div>
                {locationType === "studio" && (
                  <div className="space-y-3 rounded-xl border border-border bg-accent/30 p-4">
                    {studioImage && (
                      <div className="rounded-lg overflow-hidden border border-border/60 bg-muted">
                        <img src={studioImage} alt={t("booking.studioName")} className="w-full h-40 object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin size={16} className="mt-0.5 text-primary shrink-0" />
                      <div>
                        <p className="font-medium text-foreground">{t("booking.studioName")}</p>
                        <p className="text-muted-foreground">{studioAddress}</p>
                      </div>
                    </div>
                    <MiniMap coords={studioCoords} label={`${t("booking.studioName")}<br/>${studioAddress}`} />
                  </div>
                )}
                {locationType === "home" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t("booking.zone")}</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {zones.map((z) => (
                          <button key={z.id} onClick={() => setSelectedZone(z.id)}
                            className={`rounded-lg border p-3 text-left text-sm transition-all ${
                              selectedZone === z.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                            }`}>
                            <span className="font-medium text-foreground">{z.name}</span><br />
                            <span className="text-xs text-muted-foreground">{z.base_fee === 0 ? t("booking.noFee") : `+€${z.base_fee.toFixed(2)}`}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t("booking.address")}</Label>
                      <div className="flex gap-2">
                        <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t("booking.addressPlaceholder")} className="flex-1" />
                        <Button type="button" variant="outline" size="icon" onClick={handleGetLocation} disabled={loadingGps} title={t("booking.getLocation")} className="shrink-0">
                          {loadingGps ? <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> : <Locate size={18} />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={12} /> {t("booking.gpsHint")}</p>
                    </div>
                    <MiniMap coords={homeCoords} label={address || t("booking.address")} loading={homeMapLoading} />
                  </div>
                )}
              </div>
            )}


            {/* Confirm Step */}
            {step === confirmStep && selectedServices.length > 0 && selectedDate && (
              <div className="space-y-5">
                <h3 className="font-display text-xl font-semibold">{t("booking.confirm")}</h3>
                <div className="rounded-xl border border-border p-5 space-y-3">
                  {/* Services list */}
                  <div className="space-y-2">
                    {selectedServices.map((s) => (
                      <div key={s.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles size={14} className="text-primary" />
                          <span className="text-sm font-medium text-foreground">{s.name}</span>
                          <span className="text-xs text-muted-foreground">{s.duration_minutes} min</span>
                        </div>
                        <span className="text-sm text-foreground">€{s.price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-border pt-3 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground"><User size={14} /><span>{clientName}</span></div>
                    <div className="flex items-center gap-2 text-muted-foreground"><Mail size={14} /><span>{clientEmail}</span></div>
                    <div className="flex items-center gap-2 text-muted-foreground"><Phone size={14} /><span>{clientPhone}</span></div>
                    <div className="flex items-center gap-2 text-muted-foreground"><CalendarDays size={14} /><span>{format(selectedDate, "d MMMM yyyy", { locale: dateLocale })}</span></div>
                    <div className="flex items-center gap-2 text-muted-foreground"><Clock size={14} /><span>{selectedTime}</span></div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin size={14} /><span>{locationType === "home" ? `${t("booking.home")}, ${zone?.name || ""}` : t("booking.studioName")}</span>
                    </div>
                    {locationType === "home" && address && (
                      <div className="flex items-start gap-2 text-muted-foreground"><Home size={14} className="mt-0.5 shrink-0" /><span className="text-xs">{address}</span></div>
                    )}
                    {selectedStaffName && (
                      <div className="flex items-center gap-2 text-muted-foreground"><Users size={14} /><span>{selectedStaffName}</span></div>
                    )}
                  </div>
                  <div className="border-t border-border pt-3 space-y-1 text-sm">
                     <div className="flex justify-between"><span className="text-muted-foreground">{selectedServices.length} {t("booking.services")} · {totalDuration} min</span><span>€{servicesSubtotal.toFixed(2)}</span></div>
                     {deliveryFee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{t("booking.displacementFee")}</span><span>€{deliveryFee.toFixed(2)}</span></div>}
                    <div className="flex justify-between font-bold text-base pt-1"><span>{t("booking.total")}</span><span className="text-primary">€{total.toFixed(2)}</span></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("booking.notes")}</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("booking.notesPlaceholder")} rows={3} />
                </div>
              </div>
            )}

            </div>

            {/* Navigation */}
            <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
              {step > 0 ? (
                <Button variant="ghost" onClick={() => setStep(step - 1)} className="gap-2"><ArrowLeft size={16} /> {t("booking.previous")}</Button>
              ) : <div />}
              {step < confirmStep ? (
                <Button disabled={!canAdvance[step]} onClick={() => setStep(step + 1)} className="gap-2 rounded-full px-6">
                  {t("booking.next")} <ArrowRight size={16} />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="gap-2 rounded-full px-8">
                  {submitting ? t("booking.submitting") : t("booking.confirm")} <CheckCircle size={16} />
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
